import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio, FileText, Brain, CheckSquare, AlertTriangle, GitBranch,
  Check, ArrowRight, X, Sparkles, Loader2
} from 'lucide-react';

// Transcript lines for the simulation (speaker turns)
const TRANSCRIPT_LINES = [
  { speaker: 'Alex Rivera', text: "Let's kick off the Q2 product strategy discussion. We've got a lot to cover." },
  { speaker: 'Priya Sharma', text: 'Our user research shows a 40% increase in demand for API integrations. Enterprise customers are specifically asking for Salesforce and HubSpot connectors.' },
  { speaker: 'David Chen', text: 'From an engineering standpoint, we can deliver the Salesforce integration in 6 weeks. HubSpot might take 8 weeks due to their webhook complexity.' },
  { speaker: 'Alex Rivera', text: "That's within our Q2 timeline. What about the mobile app everyone's been requesting?" },
  { speaker: 'Maria Lopez', text: "Mobile has been our #1 feature request for 3 months running. I'd recommend we start with iOS first — 73% of our enterprise users are on Apple devices." },
  { speaker: 'David Chen', text: "I'd flag a risk there. Our current API isn't optimized for mobile payloads. We'd need to refactor the response format first, which adds 2-3 weeks." },
  { speaker: 'Priya Sharma', text: "There's also a compliance angle. If we're handling meeting recordings on mobile, we need to update our SOC 2 documentation." },
  { speaker: 'Alex Rivera', text: "Good catch. Let's make the API refactor a prerequisite. David, can you lead that?" },
  { speaker: 'Maria Lopez', text: 'I want to flag that three enterprise deals worth $450K combined are contingent on the Salesforce integration shipping by end of Q2.' },
  { speaker: 'Alex Rivera', text: "That makes it our top priority. Let's lock in the timeline: Salesforce integration by May 30th, mobile API refactor by June 15th, iOS beta by end of Q2." },
  { speaker: 'David Chen', text: 'Yes — our lead backend engineer is going on paternity leave in May. We need a succession plan for the integration work.' },
  { speaker: 'Maria Lopez', text: 'And from a competitive standpoint, Competitor X just announced a similar integration suite. We need to move fast.' },
];

const AI_SUMMARY = 'Q2 product strategy focused on two major initiatives: Salesforce/HubSpot integrations (driven by enterprise demand) and iOS mobile app launch. Three enterprise deals worth $450K are contingent on Salesforce integration shipping by May 30th.';

const AI_TASKS = [
  { title: 'Build Salesforce CRM integration connector', priority: 'high', owner: 'David Chen' },
  { title: 'Refactor API response format for mobile', priority: 'high', owner: 'David Chen' },
  { title: 'Update SOC 2 compliance documentation', priority: 'medium', owner: 'Priya Sharma' },
  { title: 'Design iOS mobile app wireframes', priority: 'medium', owner: 'Maria Lopez' },
  { title: 'Create succession plan for engineer leave', priority: 'high', owner: 'Alex Rivera' },
  { title: 'Competitive analysis: Competitor X suite', priority: 'low', owner: 'Maria Lopez' },
];

const AI_RISKS = [
  { title: 'Lead engineer on leave during critical integration phase', severity: 'high' },
  { title: '$450K pipeline at risk if Salesforce integration slips', severity: 'high' },
  { title: 'Competitor X announced similar features — timing pressure', severity: 'medium' },
];

const AI_DECISIONS = [
  { title: 'Prioritize Salesforce integration as top Q2 deliverable' },
  { title: 'Start iOS app with API refactor as prerequisite' },
  { title: 'Weekly integration check-ins led by Priya' },
];

const STAGES = [
  { id: 'fetching', label: 'Fetching Recording', icon: Radio },
  { id: 'transcribing', label: 'Transcribing Audio', icon: FileText },
  { id: 'analyzing', label: 'AI Analysis', icon: Brain },
  { id: 'structuring', label: 'Structuring Outputs', icon: GitBranch },
  { id: 'complete', label: 'Complete', icon: Check },
];

const SPEAKER_COLORS = {
  'Alex Rivera': 'text-blue-400',
  'Priya Sharma': 'text-emerald-400',
  'David Chen': 'text-amber-400',
  'Maria Lopez': 'text-pink-400',
};

export const PipelineLiveView = ({ onClose, onComplete, meetingId }) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [visibleTasks, setVisibleTasks] = useState(0);
  const [visibleRisks, setVisibleRisks] = useState(0);
  const [visibleDecisions, setVisibleDecisions] = useState(0);
  const [done, setDone] = useState(false);
  const transcriptRef = useRef(null);
  const mountedRef = useRef(true);

  // Orchestrate the entire pipeline timeline
  useEffect(() => {
    mountedRef.current = true;
    const timers = [];

    const t = (fn, ms) => { const id = setTimeout(() => { if (mountedRef.current) fn(); }, ms); timers.push(id); };

    // Stage 0: Fetching (0-1.2s)
    // Stage 1: Transcribing (1.2s)
    t(() => setCurrentStage(1), 1200);

    // Stream transcript lines (1.4s - 5.5s, ~350ms each)
    TRANSCRIPT_LINES.forEach((_, i) => {
      t(() => setVisibleLines(i + 1), 1400 + i * 340);
    });

    // Stage 2: Analyzing (3.5s — overlaps with transcript)
    t(() => setCurrentStage(2), 3500);

    // Show summary (4s)
    t(() => setShowSummary(true), 4000);

    // Show tasks one by one (4.5s - 6.5s)
    AI_TASKS.forEach((_, i) => {
      t(() => setVisibleTasks(i + 1), 4500 + i * 320);
    });

    // Show risks (5.8s - 6.6s)
    AI_RISKS.forEach((_, i) => {
      t(() => setVisibleRisks(i + 1), 5800 + i * 300);
    });

    // Stage 3: Structuring (6.5s)
    t(() => setCurrentStage(3), 6500);

    // Show decisions (6.8s - 7.4s)
    AI_DECISIONS.forEach((_, i) => {
      t(() => setVisibleDecisions(i + 1), 6800 + i * 250);
    });

    // Stage 4: Complete (7.8s)
    t(() => { setCurrentStage(4); setDone(true); }, 7800);

    return () => { mountedRef.current = false; timers.forEach(clearTimeout); };
  }, []);

  // Typewriter effect for current line
  useEffect(() => {
    if (visibleLines === 0) return;
    const line = TRANSCRIPT_LINES[visibleLines - 1];
    if (!line) return;
    let charIdx = 0;
    setTypingText('');
    const interval = setInterval(() => {
      if (!mountedRef.current) { clearInterval(interval); return; }
      charIdx++;
      setTypingText(line.text.slice(0, charIdx));
      if (charIdx >= line.text.length) clearInterval(interval);
    }, 12);
    return () => clearInterval(interval);
  }, [visibleLines]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [visibleLines, typingText]);

  const handleComplete = () => {
    if (onComplete) onComplete(meetingId);
  };

  const stageProgress = ((currentStage + 1) / STAGES.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-[#070a12] flex flex-col"
      data-testid="pipeline-live-view"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/30 to-cyan-500/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-sm">AI Pipeline — Live Processing</h2>
            <p className="text-xs text-gray-500">Q2 Product Strategy Review</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="pipeline-close-btn">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Progress stages */}
      <div className="px-6 py-3 border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-1 mb-2">
          {STAGES.map((stage, i) => {
            const Icon = stage.icon;
            const isActive = i === currentStage;
            const isDone = i < currentStage;
            return (
              <div key={stage.id} className="flex items-center gap-1 flex-1">
                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${
                  isDone ? 'bg-green-500/15 text-green-400' :
                  isActive ? 'bg-indigo-500/15 text-indigo-300' :
                  'bg-white/5 text-gray-600'
                }`}>
                  {isDone ? <Check className="w-3 h-3" /> : isActive ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
                  <span className="hidden md:inline">{stage.label}</span>
                </div>
                {i < STAGES.length - 1 && (
                  <div className={`h-px flex-1 mx-1 transition-colors duration-500 ${isDone ? 'bg-green-500/40' : 'bg-white/10'}`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
            animate={{ width: `${stageProgress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Split screen content */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Transcript stream */}
        <div className="w-1/2 border-r border-white/5 flex flex-col">
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-white">Live Transcript</span>
            {currentStage >= 1 && currentStage < 4 && (
              <span className="flex items-center gap-1 ml-auto text-xs text-cyan-400">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                Streaming
              </span>
            )}
          </div>
          <div ref={transcriptRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 scroll-smooth" data-testid="pipeline-transcript">
            {currentStage === 0 && (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching recording from Zoom...
              </div>
            )}
            {TRANSCRIPT_LINES.slice(0, Math.max(0, visibleLines - 1)).map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm"
              >
                <span className={`font-medium ${SPEAKER_COLORS[line.speaker] || 'text-gray-400'}`}>
                  {line.speaker}:
                </span>
                <span className="text-gray-300 ml-2">{line.text}</span>
              </motion.div>
            ))}
            {/* Current line with typewriter */}
            {visibleLines > 0 && visibleLines <= TRANSCRIPT_LINES.length && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-sm">
                <span className={`font-medium ${SPEAKER_COLORS[TRANSCRIPT_LINES[visibleLines - 1]?.speaker] || 'text-gray-400'}`}>
                  {TRANSCRIPT_LINES[visibleLines - 1]?.speaker}:
                </span>
                <span className="text-gray-300 ml-2">{typingText}</span>
                <span className="inline-block w-0.5 h-4 bg-cyan-400 animate-pulse ml-0.5 align-middle" />
              </motion.div>
            )}
          </div>
        </div>

        {/* RIGHT: AI Extraction */}
        <div className="w-1/2 flex flex-col">
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
            <Brain className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-medium text-white">AI Extraction</span>
            {currentStage >= 2 && currentStage < 4 && (
              <span className="flex items-center gap-1 ml-auto text-xs text-indigo-400">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                Processing
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-testid="pipeline-extraction">
            {currentStage < 2 && (
              <div className="flex items-center justify-center h-full text-gray-600 text-sm">
                <div className="text-center">
                  <Brain className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Waiting for transcript data...</p>
                </div>
              </div>
            )}

            {/* Summary */}
            <AnimatePresence>
              {showSummary && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="pipeline-summary"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-indigo-400">Summary</span>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/10">
                    {AI_SUMMARY}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tasks */}
            {visibleTasks > 0 && (
              <div data-testid="pipeline-tasks">
                <div className="flex items-center gap-2 mb-2">
                  <CheckSquare className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Tasks Extracted</span>
                  <span className="text-xs text-gray-600 ml-auto">{visibleTasks}/{AI_TASKS.length}</span>
                </div>
                <div className="space-y-1.5">
                  {AI_TASKS.slice(0, visibleTasks).map((task, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25 }}
                      className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.03] border border-white/5"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        task.priority === 'high' ? 'bg-red-400' : task.priority === 'medium' ? 'bg-amber-400' : 'bg-gray-400'
                      }`} />
                      <span className="text-xs text-gray-300 flex-1 truncate">{task.title}</span>
                      <span className="text-[10px] text-gray-600">{task.owner}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Risks */}
            {visibleRisks > 0 && (
              <div data-testid="pipeline-risks">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-red-400">Risks Detected</span>
                  <span className="text-xs text-gray-600 ml-auto">{visibleRisks}/{AI_RISKS.length}</span>
                </div>
                <div className="space-y-1.5">
                  {AI_RISKS.slice(0, visibleRisks).map((risk, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25 }}
                      className="flex items-center gap-2 p-2 rounded-lg bg-red-500/[0.04] border border-red-500/10"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${risk.severity === 'high' ? 'bg-red-400' : 'bg-amber-400'}`} />
                      <span className="text-xs text-red-300/80">{risk.title}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Decisions */}
            {visibleDecisions > 0 && (
              <div data-testid="pipeline-decisions">
                <div className="flex items-center gap-2 mb-2">
                  <GitBranch className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">Decisions</span>
                </div>
                <div className="space-y-1.5">
                  {AI_DECISIONS.slice(0, visibleDecisions).map((d, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25 }}
                      className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/[0.04] border border-blue-500/10"
                    >
                      <Check className="w-3 h-3 text-blue-400 flex-shrink-0" />
                      <span className="text-xs text-blue-300/80">{d.title}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom completion bar */}
      <AnimatePresence>
        {done && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="flex-shrink-0 border-t border-white/5 bg-gradient-to-r from-green-500/5 to-cyan-500/5 px-6 py-4 flex items-center justify-between"
            data-testid="pipeline-complete-bar"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Processing Complete</p>
                <p className="text-xs text-gray-400">{AI_TASKS.length} tasks, {AI_RISKS.length} risks, {AI_DECISIONS.length} decisions extracted</p>
              </div>
            </div>
            <button
              onClick={handleComplete}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              data-testid="pipeline-view-meeting-btn"
            >
              View Meeting
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
